import { numberToLocalString } from "@sembark-travel/number-utils"
import {
	Alert,
	Box,
	Icons,
	Container,
	Inline,
	Heading,
	Col,
	Grid,
	Stack,
	Spinner,
	Button,
	Badge,
	Text,
	RelativeTime,
	Dropdown,
	Tabs,
	TabsList,
	TabContent,
} from "@sembark-travel/ui-base"
import {
	Breadcrumbs,
	ButtonLink,
	Link,
	NavLink,
	queryToSearch,
} from "@sembark-travel/ui-router"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import useSWR from "swr"
import { AccountsList, IAccount } from "../Accounting"
import { PERMISSIONS, useCheckPermissions, useHasFeatureFlag } from "../Auth"
import { ContactsList, IContact, SelectContact } from "../Contacts"
import { generatePath } from "../router-utils"
import { EditItemInDialog } from "./EditItem"
import { ITripSource } from "./store"
import { AddressItem } from "./../Addresses"
import { EditTripSourceTagsDialog } from "./Tag"
import { DisableEnableTripSource } from "./List"
import { utcTimestampToLocalDateTimeString } from "@sembark-travel/datetime-utils"
import { Fragment, useMemo } from "react"
import { PaymentsList } from "../Payments"
import { TagBadges } from "../Tags"

function XHR(xhr: XHRInstance) {
	return {
		async get(id: string | number, params: unknown) {
			return xhr
				.get<{ data: ITripSource }>(`/trip-sources/${id}`, { params })
				.then((resp) => resp.data.data)
		},
		async storeContact(id: string | number, data: object) {
			return xhr
				.post<{ data: IContact }>("/trip-source-contacts", {
					...data,
					trip_source_id: id,
				})
				.then((resp) => resp.data.data)
		},
	}
}

export function SelectTripSourceContact({
	tripSourceId,
	onCreateSuccess,
	...props
}: Omit<React.ComponentProps<typeof SelectContact>, "onCreate" | "options"> & {
	tripSourceId: number | string
	onCreateSuccess?: (contact: IContact) => void
}) {
	const xhr = useXHR()
	const { data, mutate: revalidate } = useSWR(
		`/trip-sources/${tripSourceId}?select-contact`,
		() =>
			XHR(xhr).get(tripSourceId, {
				include: "contacts",
			})
	)
	return (
		<>
			<SelectContact
				{...props}
				phoneNumberRequired
				emailRequired
				options={(data?.contacts || []).filter((c) => !c.deleted_at) || []}
				onCreate={async (data) => {
					const contact = await XHR(xhr).storeContact(tripSourceId, data)
					revalidate()
					onCreateSuccess?.(contact)
					return contact
				}}
			/>
		</>
	)
}

function useTripSource(tripSourceId?: string, shouldFetch = false) {
	const xhr = useXHR()
	const {
		data: tripSource,
		mutate: fetchTripSource,
		error,
	} = useSWR(
		shouldFetch && tripSourceId ? `/api/trip-sources/${tripSourceId}` : null,
		() =>
			XHR(xhr).get(tripSourceId || "", {
				include: "trips_count,connected_tenant,contacts,accounts",
			})
	)
	const isFetching = !tripSource && !error
	return {
		isFetching,
		tripSource,
		error,
		fetchTripSource,
	}
}
export function TripSourceDetailsContainer({
	tripSourceId,
	children,
}: {
	tripSourceId: string
	children?: (props: {
		tripSource: ITripSource
		revalidate: () => void
	}) => React.ReactNode
}) {
	const { tripSource, isFetching, fetchTripSource } = useTripSource(
		tripSourceId,
		true
	)
	const { hasPermission } = useCheckPermissions()
	const canAccessCallLogs = useHasFeatureFlag("telecalling")
	if (isFetching && !tripSource) return <Spinner alignCenter padding="4" />
	if (!tripSource) return <Box>Trip source does not exists</Box>
	const { name, is_agent } = tripSource
	return (
		<Box>
			<Breadcrumbs
				title={name}
				items={[[generatePath("/org/trip-sources"), "Trip-Sources"]]}
			/>
			<Box>
				<Tabs layout="col" gap="0">
					<TabsList>
						<NavLink
							to={generatePath("/trip-sources/:tripSourceId", {
								tripSourceId,
							})}
							end
						>
							Details
						</NavLink>

						{hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS) ? (
							<NavLink
								to={generatePath("/trip-sources/:tripSourceId/trips", {
									tripSourceId,
								})}
							>
								Trips
							</NavLink>
						) : null}
						<NavLink
							to={generatePath(
								"/trip-sources/:tripSourceId/payments/incoming",
								{
									tripSourceId,
								}
							)}
							className={
								window.location.pathname.includes("/payments/") ? "active" : ""
							}
						>
							Payments
						</NavLink>
						{hasPermission(PERMISSIONS.MANAGE_ACCOUNTING) && is_agent ? (
							<NavLink
								to={generatePath("/trip-sources/:tripSourceId/accounting", {
									tripSourceId,
								})}
							>
								Accounting
							</NavLink>
						) : null}
						{canAccessCallLogs ? (
							<NavLink
								to={generatePath("/trip-sources/:tripSourceId/call-logs", {
									tripSourceId,
								})}
							>
								Calls
							</NavLink>
						) : null}
					</TabsList>
					<TabContent>
						{children
							? children({
									tripSource,
									revalidate: fetchTripSource,
								})
							: null}
					</TabContent>
				</Tabs>
			</Box>
		</Box>
	)
}

export function TripSourceDetails({
	tripSource,
	onChange,
}: {
	tripSource: ITripSource
	onChange: (tripSourceId: number) => void
}) {
	const xhr = useXHR()
	const { hasPermission } = useCheckPermissions()
	const {
		id,
		name,
		short_name,
		contacts = [],
		trips_count = 0,
		connected_tenant,
		address,
		is_agent,
		tags,
		teams,
		created_by,
		created_at,
		updated_by,
		updated_at,
		deleted_at,
	} = tripSource
	return (
		<Fragment key={id}>
			<Inline paddingY={"2"} justifyContent={"end"} gap="4">
				{hasPermission(PERMISSIONS.ADD_TRIP_SOURCES) && deleted_at === null ? (
					<EditItemInDialog
						tripSource={tripSource}
						onSuccess={() => onChange(id)}
					>
						{({ edit }) => (
							<Button onClick={() => edit()} size="sm">
								<Icons.Pencil /> Edit
							</Button>
						)}
					</EditItemInDialog>
				) : null}
				<Dropdown alignRight>
					<Dropdown.ToggleButton level="tertiary" size="sm">
						<Icons.DotsVertical />
					</Dropdown.ToggleButton>
					<Dropdown.Menu>
						{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
							<DisableEnableTripSource
								tripSource={tripSource}
								isDisabled={deleted_at !== null}
								onSuccess={() => {
									onChange(id)
								}}
							>
								{({ onToggleStatus, isProcessing }) => (
									<Dropdown.MenuItem
										onClick={onToggleStatus}
										color={deleted_at !== null ? "success" : "danger"}
									>
										{isProcessing ? (
											<>
												<Icons.Refresh spin /> Processing...
											</>
										) : deleted_at !== null ? (
											<>
												<Icons.Refresh /> Enable
											</>
										) : (
											<>
												<Icons.Ban /> Disable
											</>
										)}
									</Dropdown.MenuItem>
								)}
							</DisableEnableTripSource>
						) : null}
					</Dropdown.Menu>
				</Dropdown>
			</Inline>
			<Container
				paddingY="6"
				bgColor={deleted_at ? "warning" : "default"}
				borderBottomWidth="1"
			>
				<Grid gap="6">
					<Col>
						<Inline alignItems="center" gap="4" flex="1">
							{connected_tenant?.profile_image_url ? (
								<Box
									as="img"
									src={connected_tenant.profile_image_url}
									alt="Logo"
									width="16"
									height="16"
								/>
							) : (
								<Box
									width="16"
									height="16"
									display="flex"
									alignItems="center"
									justifyContent="center"
									rounded="full"
									boxShadow="base"
									bgColor="subtle"
									fontSize="lg"
									fontWeight="semibold"
									borderWidth="1"
									color="muted"
									letterSpacing="wider"
								>
									{short_name?.slice(0, 2)}
								</Box>
							)}
							<Stack gap="1">
								<Stack gap="1">
									<Inline gap="4" alignItems="end">
										<Heading as="h2">
											{name} ({short_name})
										</Heading>
										<Inline gap={"1"} alignItems={"end"} flexWrap={"wrap"}>
											{is_agent ? (
												<Box>
													<Badge>B2B</Badge>
												</Box>
											) : null}
											{tags?.length ? (
												<>
													<TagBadges tags={tags} />
													{hasPermission(PERMISSIONS.MANAGE_USERS) &&
													!deleted_at ? (
														<EditTripSourceTagsDialog
															sourceId={id}
															tags={tags}
															onSuccess={() => {
																onChange(id)
															}}
														>
															{({ onEdit }) => (
																<Button
																	onClick={onEdit}
																	level={"tertiary"}
																	size="sm"
																>
																	<Icons.Pencil />
																</Button>
															)}
														</EditTripSourceTagsDialog>
													) : null}
												</>
											) : null}
										</Inline>
									</Inline>
								</Stack>
								<Box>
									<Link
										fontWeight="semibold"
										to={
											generatePath("/trips") +
											queryToSearch({
												sources: [`${id}_${name}`],
												status: "all",
											})
										}
									>
										{numberToLocalString(trips_count)}{" "}
										{pluralize("Trip", trips_count)}
									</Link>
									{deleted_at ? (
										<>
											&nbsp;&nbsp;
											<Badge
												warning
												title={`Disabled on ${utcTimestampToLocalDateTimeString(deleted_at)}`}
											>
												Disabled
											</Badge>
										</>
									) : null}
								</Box>
								<Box maxWidth={"sm"}>
									<Text as="span" fontWeight="semibold">
										Teams:&nbsp;
									</Text>
									<Text as="span" color="muted">
										{(teams || []).length > 0
											? (teams || []).map((team) => team.name).join(" • ")
											: "N/A"}
									</Text>
								</Box>
							</Stack>
						</Inline>
					</Col>
					<Col xs={12} sm={6}>
						<Inline flexWrap="wrap" gap="6">
							{address ? (
								<AddressItem
									address={address}
									callLoggableId={id}
									callLoggableType="trip_sources"
								/>
							) : null}
							<Inline gap="4">
								<Stack gap="1">
									<Text fontSize="sm">Created By</Text>
									<Text>{created_by?.name}</Text>
									<Text fontSize="sm" color="muted">
										<RelativeTime timestamp={created_at} />
									</Text>
								</Stack>
								{updated_at && updated_at !== created_at && updated_by ? (
									<Stack gap="1">
										<Text fontSize="sm">Last Updated By</Text>
										<Text>{updated_by.name}</Text>
										<Text fontSize="sm" color="muted">
											<RelativeTime timestamp={updated_at} />
										</Text>
									</Stack>
								) : null}
							</Inline>
						</Inline>
					</Col>
				</Grid>
			</Container>
			<Container paddingY="6">
				<Stack gap="4">
					{is_agent && !contacts.length ? (
						<Alert status="warning" title="B2B Agent without Contacts!!">
							Consider adding atleast one contact to ease query creation and
							communication with this business.
						</Alert>
					) : null}
					<ContactsList
						contacts={contacts}
						onChange={() => onChange(id)}
						phoneNumberRequired
						emailRequired
						onCreate={async (data) => {
							const contact = await XHR(xhr).storeContact(id, data)
							onChange(id)
							return contact
						}}
						readOnly={!!deleted_at}
					/>
				</Stack>
			</Container>
		</Fragment>
	)
}

export function TripSourcePayments({
	tripSourceId,
	paymentType,
}: {
	tripSourceId: number | string
	paymentType: "debit" | "credit"
}) {
	const tripSourceIds = useMemo(() => {
		if (!tripSourceId) return []
		return [Number(tripSourceId)]
	}, [tripSourceId])
	if (!tripSourceId) return null
	return (
		<Box>
			<PaymentsList
				tripSourceIds={tripSourceIds}
				debit={paymentType === "debit" ? true : undefined}
				paymentContext="tripSource"
				credit={paymentType === "credit" ? true : undefined}
			/>
		</Box>
	)
}

interface IAccountingSectionProps {
	accounts?: IAccount[]
	paymentsPath: string
	emptyTitle?: string
	emptyDescription?: string
}

function AccountingSection({
	accounts,
	paymentsPath,
	emptyTitle = "No accounting activities found",
	emptyDescription = "Log payments to manage accounting.",
}: IAccountingSectionProps) {
	const hasAccounts = accounts?.length

	return (
		<Box paddingTop="4">
			<Container>
				{hasAccounts ? (
					<>
						<Box textAlign="right" marginBottom="2">
							<ButtonLink to={paymentsPath}>
								View Due Payments <Icons.ChevronDown rotate="270" />
							</ButtonLink>
						</Box>

						<AccountsList accounts={accounts} />
					</>
				) : (
					<Stack gap="4">
						<Text fontSize="md" fontWeight="semibold">
							{emptyTitle}
						</Text>

						<Text color="muted">{emptyDescription}</Text>

						<Box>
							<ButtonLink to={paymentsPath} status="primary">
								View Due Payments <Icons.ChevronDown rotate="270" />
							</ButtonLink>
						</Box>
					</Stack>
				)}
			</Container>
		</Box>
	)
}

export function TripSourceAccountings({
	tripSource,
}: {
	tripSource: ITripSource
}) {
	const paymentsLink = generatePath(
		"/trip-sources/:tripSourceId/payments/incoming",
		{ tripSourceId: String(tripSource.id) }
	)

	return (
		<AccountingSection
			accounts={tripSource.accounting_accounts}
			paymentsPath={paymentsLink}
			emptyTitle="No accounting activities for this trip source"
			emptyDescription="Log payments or adjustments related to this trip source to manage accounting records."
		/>
	)
}
